import { ListIcon } from '@phosphor-icons/react';
import type { JSX } from 'react';

import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { NotificationsBell } from '@/components/layout/NotificationsBell';
import { UserMenu } from '@/components/layout/UserMenu';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { Button } from '@/components/ui/button';

/**
 * Sticky topbar (h-14 / 56px). Per UI Guide §10.1.
 *
 * Layout: [SidebarToggle] [Breadcrumbs] [flex-1 spacer] [ThemeToggle] [Bell] [UserMenu]
 *
 * Desktop toggle: dispatches `'aryan:sidebar:toggle'` (Sidebar collapses/expands).
 * Mobile (<md): dispatches `'aryan:sidebar:mobile:open'` (Sheet drawer opens).
 * The button is always visible; the event differs based on viewport width so the same
 * button works on both form factors without needing a window-resize listener here.
 */
export function Topbar(): JSX.Element {
  const onToggleSidebar = (): void => {
    // On mobile (<768px) open the drawer; on desktop collapse/expand the sidebar
    if (window.innerWidth < 768) {
      window.dispatchEvent(new Event('aryan:sidebar:mobile:open'));
    } else {
      window.dispatchEvent(new Event('aryan:sidebar:toggle'));
    }
  };

  return (
    <header className="sticky top-0 z-10 flex h-14 items-center gap-2 border-b border-border bg-background px-4 lg:px-6">
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleSidebar}
        aria-label="Toggle sidebar"
        data-testid="sidebar-toggle"
        className="shrink-0"
      >
        <ListIcon size={18} weight="bold" />
      </Button>
      <Breadcrumbs className="min-w-0" />
      <div className="flex-1" />
      <ThemeToggle />
      <NotificationsBell />
      <UserMenu />
    </header>
  );
}
